import { ProviderType } from 'features/connections'
import { MapDataset } from 'features/maps/types'
import { buildAnalysisQueryFromSource, checkGreaterOrEqualATVersion } from '../../utils'
import { TradeAreasMode, TradeAreasType } from '../types'
import { generateQuery as generateQueryForRedshift } from './redshift'
import { generateQuery as generateQueryForSnowflake } from './snowflake'
import { generateQuery as generateQueryForBigQuery } from './bigquery'

export interface TradeAreasSettingsQuery {
  mainSource: MapDataset & { fields; metadata }
  mode: TradeAreasMode
  type: TradeAreasType
  atLocation: string
  atVersion?: string | null
  distance?: number
  time?: number
  ldsToken?: string
}

export interface TradeAreasSettingsQueryParams extends TradeAreasSettingsQuery {
  baseQuery: string
  geomField: string
  outputTable: string
  tableRegion?: string
}

export function generateTradeAreasQuery({
  mainSource,
  mode,
  type,
  time,
  distance,
  atLocation,
  atVersion,
  ldsToken
}: TradeAreasSettingsQuery): string {
  const geomField = mainSource.geoColumn || mainSource.metadata?.schema?.find((c) => c.type === 'geometry')?.name
  const baseQuery = buildAnalysisQueryFromSource({ source: mainSource })

  const payload: TradeAreasSettingsQueryParams = {
    mainSource,
    geomField,
    type,
    mode,
    time,
    distance,
    baseQuery,
    atLocation,
    atVersion,
    ldsToken,
    outputTable: '##output_table##'
  }

  const connectionProvider = mainSource.providerId
  switch (connectionProvider) {
    case ProviderType.REDSHIFT:
      return generateQueryForRedshift(payload)
    case ProviderType.SNOWFLAKE:
      return generateQueryForSnowflake(payload)
    case ProviderType.BIGQUERY:
      return generateQueryForBigQuery(payload)
    default:
      throw new Error(`Analysis not implemented for ${connectionProvider}`)
  }
}

export function generateLaunchTradeAreasQuery(provider, payload) {
  switch (provider) {
    case ProviderType.REDSHIFT:
    case ProviderType.SNOWFLAKE:
    case ProviderType.BIGQUERY:
      return payload.query.replace('##output_table##', payload.output)
    default:
      throw new Error(`Analysis not implemented for ${provider}`)
  }
}

export function tradeAreasQueryRequiresLdsToken({
  provider,
  atVersion
}: {
  provider: ProviderType
  atVersion?: string | null
}) {
  switch (provider) {
    case ProviderType.BIGQUERY:
      return true
    case ProviderType.REDSHIFT:
      return atVersion ? checkGreaterOrEqualATVersion('1.3.0', atVersion) : false
    case ProviderType.SNOWFLAKE:
      return atVersion ? checkGreaterOrEqualATVersion('1.6.0', atVersion) : false
    default:
      return false
  }
}
